/**
 * @p5doc module
 * @name utils/currency
 *
 * @description
 * # Currency
 *
 * Provides currency formatting utilities.
 *
 * 1. `getCurrencyPrice` - Formatted price with currency symbol.
 * 2. `getCurrencyValue` - Price without currency symbol.
 * 3. `formatPrice` - Formats given number with currency symbol.
 */

import {currency as cfg} from "./../config";

/**
 * @p5doc utility
 * @name getPrice
 * @module utils/currency
 *
 * @description
 * Returns formatted price with currency symbol.
 *
 * @param {number} price
 * @param {string} currencyCode
 * @returns {string}
 */
export function getPrice(price, currencyCode = "USD") {
  return formatPrice(getValue(price, currencyCode), currencyCode);
}

/**
 * @p5doc utility
 * @name getValue
 * @module utils/currency
 *
 * @description
 * Returns converted price.
 *
 * @param {number} price
 * @param {string} currencyCode
 * @returns {number}
 */
export function getValue(price, currencyCode = "USD") {
  return addPrecision(price, currencyCode);
}

/**
 * @p5doc utility
 * @name formatPrice
 * @module utils/currency
 *
 * @param {number} price
 * @param {string} currencyCode
 * @param {boolean} noDoubleZeroes
 * @returns {string}
 */
export function formatPrice(price, currencyCode = "USD", noDoubleZeroes = false) {
  let priceString, decimalPoint;

  try {
    priceString = price.toFixed(cfg[currencyCode].precision);
    decimalPoint = cfg[currencyCode].decimalPoint;
  } catch (e) {
    priceString = price.toFixed(2);
    return `${priceString} ${currencyCode}`;
  }

  if (decimalPoint !== ".") {
    priceString = priceString.replace(".", decimalPoint);
  }

  priceString = priceString.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + cfg[currencyCode].thousandsSeparator);

  if (noDoubleZeroes && priceString.substr(-3) === `${decimalPoint}00`) {
    priceString = priceString.substr(0, priceString.length - 3);
  }

  if (cfg[currencyCode].placement === 0) {
    return `${cfg[currencyCode].symbol}${priceString}`;
  } else {
    return `${priceString} ${cfg[currencyCode].symbol}`;
  }
}

/**
 * @private
 * @param {number} price
 * @param {string} currencyCode
 * @returns {number}
 */
function addPrecision(price, currencyCode = "USD") {
  let precision = 2;

  try {
    precision = Math.pow(10, cfg[currencyCode].precision);
  } catch (e) {}

  return Math.round(price * precision) / precision; // Round to `precision` number of decimal points.
}
